/**
 * client/src/lib/snapshotSync.ts
 *
 * Persists the entire client store to Convex as a JSON snapshot.
 *
 * Why a snapshot pattern: the client store has thousands of mutations across
 * 9000 lines. Migrating each mutation to a backend function is multi-day work.
 * A snapshot lets every existing mutation keep working untouched while the
 * backend holds a durable copy that survives reload, device switch, and deploy.
 *
 * Backend: convex/snapshots.ts. This previously issued browser fetches to the
 * snapshot save and load routes on an Express server that was never deployed.
 * vercel.json rewrote
 * every path to the SPA shell, so GET returned `200 text/html` and POST
 * returned 405. `res.ok` was true, `res.json()` threw, and the throw landed in
 * a catch that reported the error to another dead endpoint. The app had zero
 * durable storage and said nothing about it.
 *
 * Lifecycle:
 *   1. On boot, after auth is known, hydrateFromServer() asks Convex for the
 *      latest snapshot and replaces the in-memory DB if one exists.
 *   2. Only if that hydrate SUCCEEDED, startAutoSave() subscribes to store
 *      changes and pushes a debounced snapshot when the store is dirty.
 *   3. Sign-out cancels the auto-save and clears the dirty flag.
 *
 * The ordering in step 2 is a data-safety requirement, not a preference. See
 * hydrateFromServer's return contract below.
 */

import * as Sentry from "@sentry/react";
import { getDbSnapshot, replaceDb, subscribeToStore } from "@/lib/store";
import { track } from "@/lib/telemetry";
import { toast } from "@/hooks/use-toast";
import { api } from "../../../convex/_generated/api";
import { getConvexClient } from "@/lib/convexClient";

const DEBOUNCE_MS = 3000;
// Surface a visible warning only after this many consecutive save failures,
// so one flaky request does not alarm a sister mid-workout.
const FAILURES_BEFORE_WARNING = 3;

let dirty = false;
let timer: ReturnType<typeof setTimeout> | null = null;
let unsubscribe: (() => void) | null = null;
let consecutiveFailures = 0;
let warnedAboutFailures = false;

/**
 * Outcome of a hydrate attempt.
 *
 * `restored`  — a snapshot existed and was loaded.
 * `empty`     — the user genuinely has no snapshot yet (first run).
 * `failed`    — we could not determine either way.
 *
 * Callers MUST NOT start auto-save on `failed`. If the load failed while a
 * good snapshot exists on the server, auto-saving the un-hydrated local store
 * would overwrite her real data with a blank one. Distinguishing `empty` from
 * `failed` is the whole point of this type -- the old code collapsed both into
 * `false` and auto-saved regardless.
 */
export type HydrateOutcome = "restored" | "empty" | "failed";

/** Pull a ConvexError's structured `code` out of an unknown thrown value. */
function convexErrorCode(err: unknown): string | null {
  const data = (err as { data?: unknown } | null)?.data;
  if (data && typeof data === "object" && typeof (data as { code?: unknown }).code === "string") {
    return (data as { code: string }).code;
  }
  return null;
}

function isAuthError(err: unknown): boolean {
  return convexErrorCode(err) === "NOT_AUTHORIZED";
}

function report(stage: string, err: unknown): void {
  console.error(`[snapshotSync] ${stage} failed`, err);
  Sentry.captureException(err, { tags: { subsystem: "snapshotSync", stage } });
}

async function saveNow(): Promise<void> {
  if (!dirty) return;

  const client = getConvexClient();
  if (!client) {
    dirty = true;
    noteFailure(new Error("Convex client unavailable (VITE_CONVEX_URL unset)"));
    return;
  }

  const payload = getDbSnapshot();
  dirty = false;
  try {
    await client.mutation(api.snapshots.save, { payload: JSON.stringify(payload) });
    consecutiveFailures = 0;
    if (warnedAboutFailures) {
      warnedAboutFailures = false;
      toast({
        title: "Saved",
        description: "Your changes are syncing again.",
      });
    }
    track("snapshot_save_ok");
  } catch (err) {
    if (isAuthError(err)) {
      // Session expired. Stop the loop and let the app re-authenticate;
      // retrying would just burn requests against a dead session.
      report("save (auth)", err);
      track("snapshot_save_auth_expired");
      stopAutoSave();
      toast({
        variant: "destructive",
        title: "Signed out",
        description: "Please sign in again — recent changes may not be saved.",
      });
      return;
    }
    // Re-mark dirty so the next store change retries.
    dirty = true;
    noteFailure(err);
  }
}

/** Record a save failure, and warn the user once it stops looking transient. */
function noteFailure(err: unknown): void {
  consecutiveFailures += 1;
  report(`save (attempt ${consecutiveFailures})`, err);
  track("snapshot_save_error", {
    code: convexErrorCode(err),
    attempt: consecutiveFailures,
  });

  if (consecutiveFailures >= FAILURES_BEFORE_WARNING && !warnedAboutFailures) {
    warnedAboutFailures = true;
    const code = convexErrorCode(err);
    toast({
      variant: "destructive",
      title: "Your changes aren't saving",
      description:
        code === "PAYLOAD_TOO_LARGE"
          ? "Your journal has grown too large to sync. Please contact support."
          : "We can't reach the server. Keep this tab open — we'll keep retrying.",
    });
  }
  // Keep retrying on a timer even without further store changes, so a
  // transient outage recovers on its own instead of waiting for user input.
  scheduleSave();
}

function scheduleSave() {
  if (timer != null) return;
  timer = setTimeout(() => {
    timer = null;
    void saveNow();
  }, DEBOUNCE_MS);
}

export async function hydrateFromServer(): Promise<HydrateOutcome> {
  const client = getConvexClient();
  if (!client) {
    const err = new Error("Convex client unavailable (VITE_CONVEX_URL unset)");
    report("hydrate", err);
    track("snapshot_hydrate_error", { reason: "no_client" });
    return "failed";
  }

  try {
    const data = await client.query(api.snapshots.load, {});
    if (data?.payload) {
      replaceDb(data.payload as ReturnType<typeof getDbSnapshot>);
      track("snapshot_hydrate_ok", { updatedAt: data.updatedAt });
      return "restored";
    }
    track("snapshot_hydrate_empty");
    return "empty";
  } catch (err) {
    report("hydrate", err);
    track("snapshot_hydrate_error", { code: convexErrorCode(err) });

    // Do not toast on an auth error -- the app is about to route to sign-in
    // and a second message is noise.
    if (!isAuthError(err)) {
      toast({
        variant: "destructive",
        title: "Couldn't load your saved data",
        description:
          "We reached the server but couldn't restore your journal. Reload to try again — your saved data is untouched.",
      });
    }
    return "failed";
  }
}

/**
 * Begin auto-saving. Only call this after hydrateFromServer() returned
 * "restored" or "empty" -- see HydrateOutcome for why.
 */
/**
 * Whether auto-save is currently running, and a way to subscribe.
 *
 * Both "auto-save disabled" paths in App.tsx are correct decisions — on a
 * failed hydrate or an unresolved identity we must NOT write, or we overwrite
 * her real snapshot with a blank one. But the app stayed fully interactive and
 * every store mutation kept appearing to succeed, and the only message she saw
 * was a one-time toast wording it as a LOAD problem ("your data is safe").
 * So she could spend twenty minutes logging meals and a workout, close the tab,
 * and lose all of it, having been told the opposite.
 */
let autoSaveOn = false;
const autoSaveListeners = new Set<(on: boolean) => void>();
export function isAutoSaveOn(): boolean {
  return autoSaveOn;
}
export function onAutoSaveChange(fn: (on: boolean) => void): () => void {
  autoSaveListeners.add(fn);
  fn(autoSaveOn);
  return () => autoSaveListeners.delete(fn);
}
function setAutoSaveOn(next: boolean): void {
  if (autoSaveOn === next) return;
  autoSaveOn = next;
  autoSaveListeners.forEach((fn) => fn(next));
}

export function startAutoSave(): void {
  setAutoSaveOn(true);
  if (unsubscribe) return;
  consecutiveFailures = 0;
  warnedAboutFailures = false;
  unsubscribe = subscribeToStore(() => {
    dirty = true;
    scheduleSave();
  });
  // Flush on page hide so we don't drop the last few seconds of edits.
  // sendBeacon cannot carry a Convex auth token, so this is a best-effort
  // in-flight mutation rather than the old beacon aimed at a dead path.
  window.addEventListener("pagehide", flushOnHide);
}

function flushOnHide(): void {
  if (!dirty) return;
  void saveNow();
}

export function stopAutoSave(): void {
  setAutoSaveOn(false);
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
  if (timer != null) {
    clearTimeout(timer);
    timer = null;
  }
  window.removeEventListener("pagehide", flushOnHide);
  dirty = false;
  consecutiveFailures = 0;
  warnedAboutFailures = false;
}
